import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { Profile } from '../models/ccm/profile.model';

declare global {
  interface Window {
    pendo: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class PendoService {
  // Tapestry-internal roles (admin, auditor, quality reviewer) either carry no
  // facilities at all or span several organizations, so there is no single
  // customer organization to attribute the session to. They are grouped under
  // one account instead of being reported against whichever organization
  // happens to come first on the profile.
  private static readonly internalAccountId = 'customer-portal-internal';

  // The view shown inside the current route that the route itself does not name,
  // e.g. the active tab on a review page, and the route it was set on. Appended to
  // the URL Pendo records only while the browser is still on that route.
  private subPage: { name: string; route: string } | null = null;

  constructor(
    private router: Router,
    private authService: AuthService
  ) {
    // The API key is injected at container start from PENDO_API_KEY, so it can be
    // missing if an environment was deployed without it. Treat that as "Pendo off"
    // rather than loading the agent from a malformed CDN URL.
    if (!environment.pendo?.enabled || !environment.pendo.apiKey) {
      return;
    }

    this.loadPendoScript(environment.pendo.apiKey);
    this.initializeVisitor();
    this.trackRouteChanges();
  }

  // Loader snippet as provided by Pendo, unmodified aside from parameterizing the app key.
  private loadPendoScript(publicApiKey: string): void {
    (function (p: any, e: Document, n: string, d: string, o: any) {
      let v: string[], w: number, x: number, y: HTMLScriptElement, z: Element | null;
      o = p[d] = p[d] || {};
      o._q = o._q || [];
      v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent'];
      for (w = 0, x = v.length; w < x; ++w) {
        (function (m: string) {
          o[m] = o[m] || function (...args: any[]) {
            o._q[m === v[0] ? 'unshift' : 'push']([m].concat(args));
          };
        })(v[w]);
      }
      y = e.createElement(n) as HTMLScriptElement;
      y.async = true;
      y.src = 'https://cdn.pendo.io/agent/static/' + publicApiKey + '/pendo.js';
      z = e.getElementsByTagName(n)[0];
      z?.parentNode?.insertBefore(y, z);
    })(window, document, 'script', 'pendo', undefined);
  }

  private initializeVisitor(): void {
    this.authService.authState$
      .pipe(filter(isAuth => isAuth), take(1))
      .subscribe(() => this.identifyVisitor());
  }

  // On a fresh login, authState$ can report a valid access token slightly before
  // AuthService.claims (the decoded ID token) is populated. Since authState$ only
  // flips true once and won't re-emit for a later attempt, retry the read briefly
  // instead of silently giving up on the first miss.
  //
  // userRole and the profile are also retried here: unlike the CCM app — where roles
  // come straight off the decoded JWT — portal populates AuthService.userRole and the
  // profile (which carries the organization and facility assignments) from the /profile
  // call, so both can lag the token. Once the retry budget is spent we identify with
  // whatever is available rather than dropping the visitor entirely.
  private identifyVisitor(attempt = 0): void {
    const claims = this.authService.claims;
    const profile = this.authService.profile;
    if ((!claims || !this.authService.userRole || !profile) && attempt < 5) {
      setTimeout(() => this.identifyVisitor(attempt + 1), 500);
      return;
    }

    if (!claims) {
      return;
    }

    window.pendo.initialize({
      visitor: {
        id: claims['sub'] || claims['preferred_username'] || 'unknown',
        email: claims['email'] || '',
        full_name: claims['name'] || claims['preferred_username'] || '',
        role: this.authService.userRole || '',
        facilityIds: this.resolveFacilityIds(profile),
      },
      account: {
        id: this.resolveAccountId(profile),
      },
      events: {
        ready: () => this.trackSubPages(),
      },
    });
  }

  // Names the view shown inside the current route (e.g. a tab label), or clears it with
  // null. The label is slugged into a path segment: 'Treatment Plan' -> 'treatment-plan'.
  setSubPage(label: string | null | undefined): void {
    const slug = (label ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    this.subPage = slug ? { name: slug, route: PendoService.splitHash(window.location.hash).route } : null;
  }

  // Tabs switch views without changing the route, so Pendo would record every tab as
  // the same page and never list them under Untagged URLs. The Location API lets us
  // hand Pendo our own URL instead. setUrl is given a function, which Pendo polls
  // (every 0.5s) and records a page load whenever its value changes, so both route
  // changes and tab changes are picked up. pendo.location only exists once the agent
  // has loaded — the loader stub does not queue it — hence the ready callback.
  private trackSubPages(): void {
    window.pendo?.location?.setUrl?.(() => this.resolvePageUrl());
  }

  // The browser URL with the sub-page appended to the route, e.g.
  // #/ccm/patients/review-page/123 -> #/ccm/patients/review-page/123/vitals.
  // The app uses hash routing, so the route (and any query string) lives in the hash;
  // the sub-page goes before the query string so page rules can match on the path.
  //
  // The browser URL changes before the previous screen is torn down (and its tab view
  // clears the sub-page), so the sub-page is only appended while the route is still
  // the one it was set on — otherwise the next screen would briefly be recorded as
  // e.g. #/ccm/patients/patient-list/vitals.
  private resolvePageUrl(): string {
    const href = window.location.href;
    const url = new URL(href);
    const { route, query } = PendoService.splitHash(url.hash);
    if (!this.subPage || this.subPage.route !== route) {
      return href;
    }

    url.hash = `${route.replace(/\/+$/, '')}/${this.subPage.name}${query}`;

    return url.toString();
  }

  // Splits a hash-routing hash ('#/a/b?x=1') into its route ('#/a/b') and query ('?x=1').
  private static splitHash(hash: string): { route: string; query: string } {
    const queryStart = hash.indexOf('?');
    return queryStart === -1
      ? { route: hash, query: '' }
      : { route: hash.slice(0, queryStart), query: hash.slice(queryStart) };
  }

  // The organization assigned to the user. Every facility on a customer user's
  // profile carries the same orgid; its casing is not guaranteed (see
  // AccessControlService.canAccessFacility, which compares case-insensitively),
  // so it is normalized to stop one organization splitting into two Pendo accounts.
  private resolveAccountId(profile: Profile | undefined): string {
    const orgIds = new Set(
      (profile?.facilities ?? [])
        .map(facility => facility.orgid?.trim().toLowerCase())
        .filter((orgId): orgId is string => !!orgId)
    );

    return orgIds.size === 1 ? Array.from(orgIds)[0] : PendoService.internalAccountId;
  }

  // Facility global ids — the identifier the portal uses as facilityId elsewhere
  // (infrastructure tree nodes, /fetchStructure payloads, dashboard filters).
  private resolveFacilityIds(profile: Profile | undefined): string[] {
    const facilityIds = (profile?.facilities ?? [])
      .map(facility => (facility.gid ?? '').toString().trim())
      .filter(facilityId => !!facilityId);

    return Array.from(new Set(facilityIds));
  }

  private trackRouteChanges(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        window.pendo?.pageLoad?.();
      });
  }
}
